import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Btn, Field, TextInput, TextArea, SelectInput, Loader, EmptyState } from "../components/Primitives";
import { StatusBadge } from "../components/Badges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sparkles, FileText, CheckCircle2, Plus } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = ["website","mobile","email","sms","paper","api","call_center"];

export default function NoticeGenerator() {
  const [notices, setNotices] = useState(null);
  const [activities, setActivities] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState({ title: "", channel: "website", language: "English", version: "1.0", status: "draft", full_notice: "", short_notice: "", consent_screen_text: "" });
  const [genLoading, setGenLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState("");

  const load = async () => {
    const [n, a] = await Promise.all([api.get("/notices", { params: { limit: 200 }}), api.get("/processing-activities", { params: { limit: 500 }})]);
    setNotices(Array.isArray(n.data) ? n.data : (n.data.items || []));
    setActivities(Array.isArray(a.data) ? a.data : (a.data.items || []));
  };
  useEffect(() => { load(); }, []);

  const generateAI = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/notices/generate", { activity_id: selectedActivity || undefined, channel: form.channel });
      setForm({ ...form, full_notice: data.full_notice, short_notice: data.short_notice, consent_screen_text: data.consent_screen_text });
      toast.success("Draft generated by AI");
    } catch { toast.error("AI generation failed"); }
    setGenLoading(false);
  };

  const save = async () => {
    try {
      if (form.id) await api.put(`/notices/${form.id}`, form);
      else await api.post("/notices", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };

  const approve = async (n) => {
    try { await api.post(`/notices/${n.id}/approve`); toast.success("Approved"); load(); }
    catch { toast.error("Approve failed"); }
  };

  if (!notices) return <Loader />;

  return (
    <div>
      <PageHeader
        title="Notice Generator"
        description="AI-drafted DPDP notices — short, full, layered, and consent-screen variants."
        testId="notices-page-header"
        actions={
          <Btn data-testid="notice-create-btn" onClick={() => { setForm({ title: "", channel: "website", language: "English", version: "1.0", status: "draft", full_notice: "", short_notice: "", consent_screen_text: "" }); setSelectedActivity(""); setOpen(true); }}>
            <Plus className="w-4 h-4" /> New Notice
          </Btn>
        }
      />

      {notices.length === 0 ? <EmptyState title="No notices yet" hint="Draft your first DPDP notice, generate with AI, and version-control it." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {notices.map((n) => (
            <div key={n.id} className="bg-white border border-slate-200 rounded-md p-4 hover:border-slate-300">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{n.title}</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">{n.channel} · {n.language} · v{n.version}</div>
                </div>
                <StatusBadge status={n.status} />
              </div>
              <div className="text-xs text-slate-600 line-clamp-3 mt-2">{n.short_notice || n.full_notice?.slice(0, 200)}</div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <Btn size="sm" variant="secondary" data-testid={`notice-view-${n.id}`} onClick={() => setView(n)}><FileText className="w-3.5 h-3.5" /> View</Btn>
                <Btn size="sm" variant="secondary" data-testid={`notice-edit-${n.id}`} onClick={() => { setForm(n); setOpen(true); }}>Edit</Btn>
                {n.status !== "approved" && <Btn size="sm" data-testid={`notice-approve-${n.id}`} onClick={() => approve(n)}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</Btn>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{form.id ? "Edit" : "Create"} Notice</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto pr-1">
            <Field label="Title" required className="sm:col-span-2"><TextInput data-testid="notice-title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></Field>
            <Field label="Channel"><SelectInput value={form.channel} onChange={(e) => setForm({...form, channel: e.target.value})}>{CHANNELS.map(o => <option key={o} value={o}>{o}</option>)}</SelectInput></Field>
            <Field label="Language"><TextInput value={form.language} onChange={(e) => setForm({...form, language: e.target.value})} /></Field>
            <Field label="Version"><TextInput value={form.version} onChange={(e) => setForm({...form, version: e.target.value})} /></Field>
            <Field label="Status"><SelectInput value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}>{["draft","under_review","approved","retired"].map(o => <option key={o} value={o}>{o}</option>)}</SelectInput></Field>

            <div className="sm:col-span-2 border-t border-slate-200 pt-4 mt-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">AI Drafting</div>
              <div className="flex gap-2 items-end flex-wrap">
                <Field label="Base on activity" className="flex-1 min-w-[220px]">
                  <SelectInput data-testid="notice-activity-select" value={selectedActivity} onChange={(e) => setSelectedActivity(e.target.value)}>
                    <option value="">-- optional --</option>
                    {activities.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </SelectInput>
                </Field>
                <Btn data-testid="notice-ai-generate" onClick={generateAI} disabled={genLoading}>
                  <Sparkles className="w-4 h-4" /> {genLoading ? "Drafting..." : "Draft with AI"}
                </Btn>
              </div>
            </div>

            <Field label="Short notice" className="sm:col-span-2"><TextArea rows={3} value={form.short_notice || ""} onChange={(e) => setForm({...form, short_notice: e.target.value})} /></Field>
            <Field label="Full notice" className="sm:col-span-2"><TextArea data-testid="notice-full" rows={10} value={form.full_notice || ""} onChange={(e) => setForm({...form, full_notice: e.target.value})} /></Field>
            <Field label="Consent screen text" className="sm:col-span-2"><TextArea rows={3} value={form.consent_screen_text || ""} onChange={(e) => setForm({...form, consent_screen_text: e.target.value})} /></Field>
          </div>
          <DialogFooter>
            <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="notice-save-btn" onClick={save}>Save</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View dialog */}
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{view?.title}</DialogTitle></DialogHeader>
          <div className="max-h-[65vh] overflow-y-auto pr-1 text-sm space-y-4">
            <div><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Short notice</div><div className="whitespace-pre-wrap">{view?.short_notice}</div></div>
            <div><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Full notice</div><div className="whitespace-pre-wrap">{view?.full_notice}</div></div>
            <div><div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Consent screen text</div><div className="whitespace-pre-wrap">{view?.consent_screen_text}</div></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
